import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, Palette, Shield, Bell, LogOut, Save, Camera, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const { user, setUser, logout } = useAuth();
  const avatarInput = useRef(null);
  const bannerInput = useRef(null);
  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    bio: user?.bio || "",
    status: user?.status || "online",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [setup, setSetup] = useState(null);
  const [mfaBusy, setMfaBusy] = useState(false);

  const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", form);
      setUser(data);
      toast.success("Perfil atualizado");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const body = new FormData();
      body.append("image", file);
      const { data } = await api.post(`/users/me/media/${kind}`, body);
      setUser(data);
      toast.success(kind === "avatar" ? "Foto atualizada" : "Banner atualizado");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setUploading("");
    }
  };

  const beginMfa = async () => {
    setMfaBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup", { password });
      setSetup(data);
      setOtp("");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setMfaBusy(false);
    }
  };

  const confirmMfa = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/confirm", { code: otp });
      setUser({ ...user, mfa_enabled: true });
      setSetup(null);
      setPassword("");
      setOtp("");
      toast.success("Autenticação em dois fatores ativada");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setMfaBusy(false);
    }
  };

  const disableMfa = async () => {
    setMfaBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password, code: otp });
      setUser({ ...user, mfa_enabled: false });
      setPassword("");
      setOtp("");
      toast.success("Autenticação em dois fatores desativada");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setMfaBusy(false);
    }
  };

  return (
    <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="settings-page">
      <div className="max-w-4xl px-6 lg:px-10 py-10">
        <div className="nx-chip nx-chip-blue mb-3">Conta</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl">Configurações</h1>
        <p className="text-muted-foreground mt-2">Personalize seu perfil e proteja sua conta.</p>

        <Tabs defaultValue="perfil" className="mt-8">
          <TabsList className="nx-panel-2 p-1 h-auto rounded-full">
            {[
              { v: "perfil", l: "Perfil", i: User },
              { v: "aparencia", l: "Aparência", i: Palette },
              { v: "seguranca", l: "Segurança", i: Shield },
              { v: "notificacoes", l: "Notificações", i: Bell },
            ].map((tab) => (
              <TabsTrigger key={tab.v} value={tab.v} className="rounded-full px-4 py-1.5 text-xs uppercase tracking-widest data-[state=active]:nx-btn-blue data-[state=active]:text-white text-muted-foreground data-[state=active]:font-semibold gap-2">
                <tab.i className="w-3.5 h-3.5" /> {tab.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="perfil" className="pt-6">
            <form onSubmit={save} className="nx-panel p-6 space-y-6">
              <input ref={avatarInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload("avatar", e.target.files?.[0])} />
              <input ref={bannerInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => upload("banner", e.target.files?.[0])} />

              <button type="button" onClick={() => bannerInput.current?.click()} className="relative block w-full h-40 overflow-hidden rounded-2xl border border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel-2))] group">
                {user?.banner_url && <img src={user.banner_url} alt="Banner" className="w-full h-full object-cover" />}
                <span className="absolute inset-0 grid place-items-center bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="flex items-center gap-2 font-semibold"><Camera className="w-5 h-5" /> {uploading === "banner" ? "Enviando..." : "Trocar banner"}</span>
                </span>
              </button>

              <div className="flex items-center gap-4">
                <button type="button" onClick={() => avatarInput.current?.click()} className="relative rounded-full group">
                  <Avatar className="w-24 h-24 ring-2 ring-[hsl(var(--nx-blue))] nx-glow-blue">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="text-2xl bg-gradient-to-br from-[hsl(var(--nx-blue))] to-[hsl(var(--nx-magenta))] text-white">{user?.username?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="absolute inset-0 rounded-full grid place-items-center bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity"><Camera className="w-5 h-5" /></span>
                </button>
                <div>
                  <div className="font-semibold">{uploading === "avatar" ? "Enviando foto..." : "Trocar foto do perfil"}</div>
                  <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP ou GIF. Máximo 5 MB.</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nome exibido</Label>
                  <Input value={form.display_name} onChange={change("display_name")} maxLength={80} className="h-11 rounded-xl bg-[hsl(var(--input))]" />
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <select value={form.status} onChange={change("status")} className="w-full h-11 rounded-xl bg-[hsl(var(--input))] border border-[hsl(var(--nx-border))] px-3 text-sm">
                    <option value="online">Online</option><option value="idle">Ausente</option><option value="dnd">Não perturbe</option><option value="offline">Invisível</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Biografia</Label>
                <Textarea value={form.bio} onChange={change("bio")} maxLength={280} rows={4} placeholder="Fale um pouco sobre você e seus canais..." className="rounded-xl bg-[hsl(var(--input))]" />
                <div className="text-right text-xs text-muted-foreground">{form.bio.length}/280</div>
              </div>
              <div className="flex items-center justify-between">
                <Button type="submit" disabled={saving} className="rounded-xl nx-btn-blue border-0 h-11 px-6"><Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alterações"}</Button>
                <Button type="button" variant="ghost" onClick={logout} className="text-[hsl(var(--nx-red))]"><LogOut className="w-4 h-4 mr-2" />Sair</Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="seguranca" className="pt-6">
            <div className="nx-panel p-6 space-y-5">
              <div className="flex items-start gap-3"><KeyRound className="w-6 h-6 text-[hsl(var(--nx-cyan))]" /><div><h2 className="font-semibold text-lg">Aplicativo autenticador</h2><p className="text-sm text-muted-foreground">Use Google Authenticator, Microsoft Authenticator, Authy ou outro aplicativo TOTP.</p></div></div>
              <div className={`nx-chip ${user?.mfa_enabled ? "nx-chip-cyan" : "nx-chip-blue"}`}>{user?.mfa_enabled ? "Ativado" : "Desativado"}</div>
              {!setup && <div className="space-y-2 max-w-sm"><Label>Senha atual</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></div>}
              {setup && (
                <div className="space-y-4">
                  <p className="text-sm">Leia este QR Code no aplicativo autenticador:</p>
                  <img src={setup.qr_code} alt="QR Code para configurar autenticação" className="w-48 h-48 rounded-xl bg-white p-2" />
                  <div className="text-xs text-muted-foreground">Chave manual: <code className="select-all text-foreground">{setup.secret}</code></div>
                  <div className="space-y-2 max-w-sm"><Label>Código de 6 dígitos</Label><Input inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} /></div>
                  <Button onClick={confirmMfa} disabled={mfaBusy || otp.length !== 6} className="nx-btn-blue border-0">Confirmar e ativar</Button>
                </div>
              )}
              {!setup && !user?.mfa_enabled && <Button onClick={beginMfa} disabled={mfaBusy || !password} className="nx-btn-blue border-0">Configurar autenticação</Button>}
              {!setup && user?.mfa_enabled && (
                <div className="space-y-3 max-w-sm">
                  <Label>Código atual de 6 dígitos</Label>
                  <Input inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} />
                  <Button variant="destructive" onClick={disableMfa} disabled={mfaBusy || !password || otp.length !== 6}>Desativar autenticação</Button>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="aparencia" className="pt-6"><div className="nx-panel p-8 text-center text-muted-foreground">Personalização de tema em breve.</div></TabsContent>
          <TabsContent value="notificacoes" className="pt-6"><div className="nx-panel p-8 text-center text-muted-foreground">Preferências de notificação em breve.</div></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
